package com.pincer.app.viewmodel

import com.pincer.app.ApplicationState
import com.pincer.app.I18n
import com.pincer.core.model.tree.Answer
import com.pincer.core.model.tree.Participant
import com.pincer.core.model.tree.QuestionFormat
import com.pincer.core.model.tree.CHATS_SCOPE
import com.pincer.core.model.tree.PERMISSIONS_SCOPE
import com.pincer.core.model.tree.QUESTIONS_SCOPE
import com.pincer.core.model.tree.CANDIDATES_SCOPE
import com.pincer.core.model.tree.SELECTED_SCOPE
import com.pincer.core.model.tree.NAME_SCOPE
import com.pincer.core.model.tree.DESCRIPTION_SCOPE
import com.pincer.core.model.tree.RULES_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_ANSWERS_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_RULES_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_ROLES_SCOPE
import com.pincer.core.model.drafts.QuestionDraft
import com.pincer.core.model.drafts.AnswerDraft
import com.pincer.core.functions.termsNeeded
import com.pincer.core.functions.getAnswer
import com.pincer.core.functions.readable
import com.pincer.core.functions.readablePid
import com.pincer.core.functions.getRolesForParticipant
import com.pincer.core.functions.checkPermission
import com.pincer.core.functions.validateText
import com.pincer.core.functions.validateEmail
import com.pincer.core.functions.validateCode
import com.pincer.core.functions.validateQuestionCode
import com.pincer.core.functions.validateRegex
import com.pincer.core.functions.validateRoles
import com.pincer.core.functions.validateRole
import com.pincer.core.functions.validateOptions
import com.pincer.core.functions.validateAnswer
import com.pincer.core.functions.validateRules

// How many chat messages show before the rest go behind "earlier messages"
const val LATEST_CHATS = 10

fun pincerTakePart(state: ApplicationState, context: Node) {
    val pincer = state.pincer()
    if (pincer == null) return
    if (pincer.selected != null) {
        context.node(key = "startTime", prominence = Prominence.Primary) {
            val locKey = pincer.selected!!.getLocalisationKey(state.pincerSelf()?.zone ?: "missing", state.pincerSelf()?.locale ?: "missing")
            var wording = pincer.localisations.get(locKey) ?: pincer.selected!!.start()!!
            node("value", wording)
        }
    }
    else if (state.checkRead(CANDIDATES_SCOPE) && pincer.candidates.size > 0) {
        // A grid for renderers that can draw one; each suggestion as text, with its button, for everyone
        val slots = pincer.candidates.values.map { candidate ->
            GridSlot(
                key       = candidate.id(),
                start     = candidate.start()!!,
                minutes   = candidate.du ?: 0,
                available = (candidate.participants(pincer) - candidate.undecided(pincer)).size,
                undecided = candidate.undecided(pincer).size,
            )
        }
        val count = pincer.candidates.size
        val summary = I18n.translate(if (count == 1) "suggestions-summary-one" else "suggestions-summary", count.toString())
        context.node(key = "suggestions", wording = I18n.translate("top-candidate"), rich = Rich.AvailabilityGrid(zone = pincer.zone, slots = slots), prominence = Prominence.Primary, summary = summary) {
            for (candidate in pincer.candidates.values) {
                val locKey = candidate.getLocalisationKey(state.pincerSelf()?.zone ?: "missing", state.pincerSelf()?.locale ?: "missing")
                var candidateWording = pincer.localisations.get(locKey) ?: candidate.start()!!
                node(key = candidate.id(), wording = candidateWording) {
                    node("du", I18n.translate("candidate-minutes", "" + candidate.du))
                    val names = { people: Set<Participant> -> people.map { it.name ?: I18n.translate("someone") }.sorted().joinToString(", ") }
                    // Everyone in the candidate is in participants; those who haven't said (no green rules) are also in undecided
                    val available = candidate.participants(pincer) - candidate.undecided(pincer)
                    if (available.isNotEmpty()) node("pt", I18n.translate("candidate-available", names(available)))
                    if (candidate.undecided(pincer).isNotEmpty())    node("un", I18n.translate("candidate-undecided", names(candidate.undecided(pincer))))
                    button(key = "confirmCandidate", action = Action.ConfirmCandidate(candidate.id()))
                }
            }
        }
    }
    if (state.checkRead(CHATS_SCOPE)) {
        /*
        The latest messages, with the rest behind "earlier messages", which opens on request. A long history then
        costs nothing until someone asks for it: on screen, on the wire, or in an agent's context.
        */
        context.node("chats") {
            val chat = { chatId: String, node: Node ->
                val message = pincer.chats[chatId]!!
                val participant = pincer.participants[message.principalId]
                val name = if (participant != null && participant.name != null) participant.name!! else message.principalId
                node.node(chatId, name) {
                    node("wording", message.wording)
                }
            }
            val chatIds = pincer.chats.keys.toList()
            val earlier = chatIds.dropLast(LATEST_CHATS)
            if (earlier.isNotEmpty()) {
                val summary = I18n.translate(if (earlier.size == 1) "earlier-summary-one" else "earlier-summary", earlier.size.toString())
                opening(state = state, key = "earlier", atFirst = false, summary = summary) {
                    for (chatId in earlier) chat(chatId, this)
                }
            }
            for (chatId in chatIds.takeLast(LATEST_CHATS)) chat(chatId, this)
            node("addChatMessage") {
                val problem = validateText(state.transient.draftChat)
                input(key = "setDraftChat", action = Action.SetDraftChat, wording = state.transient.draftChat ?: "", problem = problem)
                possibleProblem(problem)
                button(key = "addChat", action = Action.AddChat)
            }
        }
    }
}
